import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { supabase } from '../../../supabase';
import { FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiDownload, FiEye, FiEyeOff, FiGlobe, FiInfo, FiLock, FiShare, FiShield, FiTrash2, FiUser } from 'react-icons/fi';
import { downloadUserDataExport } from '../dataExport';
import { fetchOwnProfile } from '../../../utils/userProfile';
import { getStrengthLevel } from '../../../utils/passwordStrength';
import BottomSheet from '../../../components/common/BottomSheet';
import LanguageSwitcher from '../../../components/common/LanguageSwitcher';
import packageJson from '../../../../package.json';
import { COUNTRY_CODES } from '../../../utils/countryCodes';
import '../components/Profile.css';
import './Settings.css';

function formatMemberSince(value, language) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(language === 'en' ? 'en-US' : 'nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Reuses the same users/{email} doc cache Home.jsx/Profile.jsx already write, so
// this form doesn't have to show "Laster..." on every single visit while the same
// getDoc that already ran moments ago re-runs.
const getCachedUserDoc = () => {
  try { return JSON.parse(localStorage.getItem('pws_user_cache')); } catch { return null; }
};

// Not locked to Norway - the stored `phone` is a full MSISDN (same convention
// MyCart's checkout already uses). Only strips the country code back off for
// display when it actually matches Norway's fixed 8-digit local length
// (mirrors smsService.js's toMsisdn() heuristic on the backend); anything
// else is shown as-is under the default '47' picker rather than guessing.
function splitStoredPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('47') && digits.length === 10) {
    return { countryCode: '47', local: digits.slice(2) };
  }
  return { countryCode: '47', local: digits };
}

export default function Settings() {
  const { t, i18n } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const cachedUser = getCachedUserDoc();
  const canUseCache = Boolean(cachedUser && localStorage.getItem('pws_auth') === '1');
  const [firstName, setFirstName] = useState(canUseCache ? (cachedUser.firstName || '') : '');
  const [lastName, setLastName] = useState(canUseCache ? (cachedUser.lastName || '') : '');
  const [email, setEmail] = useState(canUseCache ? (cachedUser.email || '') : '');
  const initialPhoneSplit = splitStoredPhone(canUseCache ? cachedUser.phone : '');
  const [phone, setPhone] = useState(initialPhoneSplit.local);
  const [countryCode, setCountryCode] = useState(initialPhoneSplit.countryCode);
  const [dob, setDob] = useState(canUseCache ? (cachedUser.dob || '') : '');
  // dob stays the source of truth as an ISO yyyy-mm-dd string (matches the DB
  // column and the upsert payload) - these three are display-only and kept in
  // sync with it, so the visible order is always day/month/year regardless of
  // browser locale, unlike a native <input type="date"> whose displayed order
  // follows the browser's own locale settings (Firefox ignores `lang` entirely).
  const [dobDay, setDobDay] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobYear, setDobYear] = useState('');
  useEffect(() => {
    const [y, m, d] = (dob || '').split('-');
    setDobYear(y || '');
    setDobMonth(m || '');
    setDobDay(d || '');
  }, [dob]);
  const updateDob = (day, month, year) => {
    if (day && month && year && year.length === 4) {
      setDob(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    } else {
      setDob('');
    }
  };
  const [memberSince, setMemberSince] = useState(canUseCache ? (cachedUser.memberSince || '') : '');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(!canUseCache);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');

  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');

  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');

  const [activeSheet, setActiveSheet] = useState(null); // null | 'export' | 'delete' | 'ios-install' | 'language'

  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user || null;
      setAuthUser(user);
      if (!user) {
        // Mirror Profile.jsx: only bounce to login once we're sure this is a
        // real logout, not just the session still restoring on refresh.
        const hasCache = Boolean(getCachedUserDoc());
        const hasAuthFlag = localStorage.getItem('pws_auth') === '1';
        if (!hasCache && !hasAuthFlag) {
          navigate('/LogIn', { replace: true });
        } else {
          setLoading(false);
        }
        return;
      }

      localStorage.setItem('pws_auth', '1');
      try {
        const profile = await fetchOwnProfile(user.id);
        if (profile) {
          setFirstName(profile.firstName);
          setLastName(profile.lastName);
          setEmail(profile.email || user.email || '');
          const phoneSplit = splitStoredPhone(profile.phone);
          setPhone(phoneSplit.local);
          setCountryCode(phoneSplit.countryCode);
          setDob(profile.dob);
          setMemberSince(profile.memberSince || user.created_at || '');
          try {
            // Merge into the shared user-doc cache instead of overwriting it, so
            // fields Home/Profile rely on (name, points) aren't clobbered.
            const existing = getCachedUserDoc() || {};
            localStorage.setItem('pws_user_cache', JSON.stringify({ ...existing, ...profile }));
          } catch {}
        } else {
          setFirstName(user.user_metadata?.display_name?.split(' ')[0] || '');
          setLastName(user.user_metadata?.display_name?.split(' ').slice(1).join(' ') || '');
          setEmail(user.email || '');
          setPhone('');
          setDob('');
          setMemberSince(user.created_at || '');
        }
      } finally {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Best-effort security notice - never blocks or fails the actual change if
  // the email itself doesn't go out.
  const notifyAccountChange = async (type, newEmail) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/account/notify-change`,
        { type, newEmail },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
    } catch (err) {
      console.error('Failed to send account-change notice:', err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!authUser) {
      setMsg(t('notLoggedInError'));
      return;
    }

    try {
      const { error: upsertError } = await supabase.from('users').upsert({
        auth_user_id: authUser.id,
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`.trim(),
        phone: phone.trim() ? countryCode + phone.trim() : '',
        dob,
      }, { onConflict: 'auth_user_id' });
      if (upsertError) throw upsertError;

      // Email changes go through Supabase separately - it requires confirming
      // the new address before the change actually takes effect (unlike
      // Firebase, which applied it immediately after reauthentication), so the
      // users row's own email column is left alone until that confirmation.
      if (email !== authUser.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email });
        if (emailError) throw emailError;
        notifyAccountChange('email', email);
        setMsg(t('changesSavedConfirmEmail'));
        return;
      }

      setMsg(t('changesSaved'));
    } catch (err) {
      setMsg(t('errorWithMessage', { message: err.message }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    if (!authUser) {
      setPasswordMsg(t('notLoggedInError'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordMsg(t('passwordsDontMatch'));
      return;
    }

    try {
      // Supabase's updateUser doesn't require re-entering the current password
      // itself, but re-verifying it here preserves the same defense-in-depth
      // UX Firebase's reauthenticateWithCredential gave (a lingering/stolen
      // session alone isn't enough to change the password).
      const { error: verifyError } = await supabase.auth.signInWithPassword({ email: authUser.email, password: oldPassword });
      if (verifyError) {
        setPasswordMsg(t('wrongPasswordTryAgain'));
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      notifyAccountChange('password');
      setPasswordMsg(t('passwordUpdated'));
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setPasswordMsg(t('errorWithMessage', { message: err.message }));
    }
  };

  const handleExportData = async () => {
    setExportMsg('');
    if (!authUser) {
      setExportMsg(t('notLoggedInError'));
      return;
    }
    setExporting(true);
    try {
      await downloadUserDataExport(authUser);
    } catch (err) {
      setExportMsg(t('exportDataError'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteMsg('');
    if (!authUser) {
      setDeleteMsg(t('notLoggedInError'));
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/account/delete`,
        { password: deletePassword },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      const backendMessage = err.response?.data?.error;
      setDeleteMsg(backendMessage === 'Feil passord' ? t('wrongPasswordTryAgain') : t('errorWithMessage', { message: backendMessage || err.message }));
    }
  };

  if (loading) {
    return (
      <div className="settings-wrapper">
        <div className="settings-container">{t('loading')}</div>
      </div>
    );
  }

  const strength = getStrengthLevel(newPassword, tc);

  return (
    <div className="settings-wrapper">
      <div className="settings-section-label"><span>{t('sectionProfile')}</span></div>

      <div className="settings-container">
        <div className="card-title">
          <div className="card-title-icon yellow"><FiUser /></div>
          {t('editProfile')}
        </div>

        <form onSubmit={handleSave} className="settings-form">
          <div className="field-grid">
            <label className="field">
              <span className="input-label">{t('firstName')}</span>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t('firstName')} />
            </label>
            <label className="field">
              <span className="input-label">{t('lastName')}</span>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t('lastName')} />
            </label>
          </div>

          <label className="field">
            <span className="input-label">{t('email')}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('yourEmail')} />
          </label>

          {/* A plain div, not <label> like the other fields - it now holds two
              separate controls (the country <select> and the number <input>),
              and a label wrapping two form controls forwards clicks ambiguously. */}
          <div className="field">
            <span className="input-label">{t('phone')}</span>
            <div className="settings-phone-row">
              <div className="settings-phone-prefix-wrap">
                {/* Same overlay pattern as MyCart's CARD picker and CreateUser's
                    signup field: visible compact flag+code span behind an
                    invisible-text <select>, native dropdown unaffected. */}
                <span className="settings-phone-prefix-display" aria-hidden="true">
                  {(COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]).flag} +{countryCode}
                </span>
                <select
                  className="settings-phone-prefix-select"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  aria-label={t('countryCodeLabel')}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name} +{c.code}</option>
                  ))}
                </select>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder={t('yourPhoneLocal')}
              />
            </div>
          </div>

          <label className="field">
            <span className="input-label">{t('birthDate')}</span>
            <div className="dob-input-group">
              <input
                type="text" inputMode="numeric" maxLength={2} placeholder="DD"
                value={dobDay}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setDobDay(v); updateDob(v, dobMonth, dobYear); }}
              />
              <span className="dob-separator">.</span>
              <input
                type="text" inputMode="numeric" maxLength={2} placeholder="MM"
                value={dobMonth}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setDobMonth(v); updateDob(dobDay, v, dobYear); }}
              />
              <span className="dob-separator">.</span>
              <input
                type="text" inputMode="numeric" maxLength={4} placeholder={i18n.language === 'en' ? 'YYYY' : 'ÅÅÅÅ'}
                value={dobYear}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setDobYear(v); updateDob(dobDay, dobMonth, v); }}
              />
            </div>
          </label>

          <label className="field">
            <span className="input-label">{t('memberSince')}</span>
            <input type="text" value={formatMemberSince(memberSince, i18n.language)} disabled readOnly />
          </label>

          <button type="submit" className="btn-primary">{t('saveChanges')}</button>
          {msg && <p className="status-msg success"><FiCheckCircle size={14} />{msg}</p>}
        </form>
      </div>

      <div className="settings-section-label"><span>{t('sectionSecurity')}</span></div>

      <div className="settings-container-pw">
        <div className="card-title">
          <div className="card-title-icon blue"><FiLock /></div>
          {t('editPassword')}
        </div>

        <form onSubmit={handlePasswordChange} className="settings-form">
          {/* Hidden username field - browsers/password managers need one in a
              password form to correctly associate it with the account, even
              when the visible fields are all passwords. */}
          <input type="email" value={email} autoComplete="username" readOnly hidden />
          <label className="field">
            <span className="input-label">{t('currentPassword')}</span>
            <div className="password-input-wrap">
              <input type={showOldPassword ? 'text' : 'password'} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder={t('currentPassword')} autoComplete="current-password" required />
              <button type="button" className="password-toggle-btn" onClick={() => setShowOldPassword(v => !v)} aria-label={showOldPassword ? t('hidePassword') : t('showPassword')}>
                {showOldPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </label>

          <label className="field">
            <span className="input-label">{t('newPassword')}</span>
            <div className="password-input-wrap">
              <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('newPassword')} autoComplete="new-password" required />
              <button type="button" className="password-toggle-btn" onClick={() => setShowNewPassword(v => !v)} aria-label={showNewPassword ? t('hidePassword') : t('showPassword')}>
                {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
            <div className="strength-bar"><div className="strength-fill" style={{ width: strength.width, background: strength.color }} /></div>
            <div className="strength-label" style={{ color: newPassword ? strength.color : '#5B6A99' }}>{strength.label}</div>
          </label>

          <label className="field">
            <span className="input-label">{t('confirmNewPassword')}</span>
            <div className="password-input-wrap">
              <input type={showConfirmPassword ? 'text' : 'password'} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder={t('confirmNewPassword')} autoComplete="new-password" required />
              <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPassword(v => !v)} aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}>
                {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </label>

          <div className="tip-box"><FiInfo /><span>{t('passwordRequirementsTip')}</span></div>

          <button type="submit" className="btn-green">{t('savePassword')}</button>
          {passwordMsg && <p className="status-msg success"><FiCheckCircle size={14} />{passwordMsg}</p>}
        </form>
      </div>

      <div className="settings-section-label"><span>{t('sectionAccount')}</span></div>

      <div className="profile-menu-card">
        <div
          className="profile-menu-row"
          role="button"
          tabIndex={0}
          onClick={() => setActiveSheet('language')}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveSheet('language'); }}
        >
          <div className="profile-menu-icon profile-menu-icon--teal"><FiGlobe size={17} /></div>
          <div className="profile-menu-text"><h4>{t('languageTitle')}</h4><p>{t('languageDesc')}</p></div>
          <FiChevronRight size={16} className="profile-menu-arrow" />
        </div>

        <div
          className="profile-menu-row"
          role="button"
          tabIndex={0}
          onClick={() => setActiveSheet('export')}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveSheet('export'); }}
        >
          <div className="profile-menu-icon profile-menu-icon--blue"><FiDownload size={17} /></div>
          <div className="profile-menu-text"><h4>{t('myDataTitle')}</h4><p>{t('myDataDesc')}</p></div>
          <FiChevronRight size={16} className="profile-menu-arrow" />
        </div>

        <div
          className="profile-menu-row"
          role="button"
          tabIndex={0}
          onClick={() => setActiveSheet('delete')}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveSheet('delete'); }}
        >
          <div className="profile-menu-icon" style={{ background: '#E05555', color: '#fff' }}><FiTrash2 size={17} /></div>
          <div className="profile-menu-text"><h4>{t('deleteAccountTitle')}</h4><p>{t('deleteAccountDesc')}</p></div>
          <FiChevronRight size={16} className="profile-menu-arrow" />
        </div>

        <div
          className="profile-menu-row"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/personvern')}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/personvern'); }}
        >
          <div className="profile-menu-icon" style={{ background: '#5B6A99', color: '#fff' }}><FiShield size={17} /></div>
          <div className="profile-menu-text"><h4>{t('privacyTitle')}</h4><p>{t('privacyDesc')}</p></div>
          <FiChevronRight size={16} className="profile-menu-arrow" />
        </div>

        <div
          className="profile-menu-row"
          role="button"
          tabIndex={0}
          onClick={() => setActiveSheet('ios-install')}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveSheet('ios-install'); }}
        >
          <div className="profile-menu-icon profile-menu-icon--yellow"><FiDownload size={17} /></div>
          <div className="profile-menu-text"><h4>{t('installAppTitle')}</h4><p>{t('installAppDesc')}</p></div>
          <FiChevronRight size={16} className="profile-menu-arrow" />
        </div>
      </div>

      <p className="profile-app-version">v{packageJson.version}</p>

      {activeSheet === 'language' && (
        <BottomSheet
          icon={<FiGlobe size={20} />}
          iconColor="blue"
          title={t('languageTitle')}
          subtitle={t('languageDesc')}
          onClose={() => setActiveSheet(null)}
        >
          <LanguageSwitcher />
        </BottomSheet>
      )}

      {activeSheet === 'export' && (
        <BottomSheet
          icon={<FiDownload size={20} />}
          iconColor="blue"
          title={t('myDataTitle')}
          subtitle={t('myDataDesc')}
          onClose={() => setActiveSheet(null)}
        >
          <p className="info-box" style={{ marginBottom: 12 }}>
            {t('myDataBody')}
          </p>

          <button type="button" className="btn-primary" onClick={handleExportData} disabled={exporting}>
            {exporting ? t('fetchingData') : t('downloadMyData')}
          </button>
          {exportMsg && <p className="status-msg muted">{exportMsg}</p>}
        </BottomSheet>
      )}

      {activeSheet === 'delete' && (
        <BottomSheet
          icon={<FiTrash2 size={20} />}
          iconColor="red"
          title={t('deleteAccountTitle')}
          subtitle={t('deleteCannotBeUndone')}
          onClose={() => {
            setActiveSheet(null);
            setShowDeleteConfirm(false);
            setDeletePassword('');
            setDeleteMsg('');
          }}
        >
          <div className="warning-box" style={{ marginBottom: 12 }}>
            <FiAlertTriangle />
            <span><strong>{t('deleteWarningLabel')}</strong> {t('deleteWarningText')}</span>
          </div>

          {!showDeleteConfirm ? (
            <button type="button" className="btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              {t('deleteMyAccount')}
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount} className="settings-form">
              <p className="info-box" style={{ marginBottom: 0 }}>{t('enterPasswordToConfirmDelete')}</p>
              <label className="field">
                <span className="input-label">{t('password')}</span>
                <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t('yourPassword')} autoComplete="current-password" required />
              </label>

              <div className="delete-actions">
                <button type="submit" className="btn-danger">{t('confirmDelete')}</button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword('');
                    setDeleteMsg('');
                  }}
                >
                  {t('cancel')}
                </button>
              </div>

              {deleteMsg && <p className="status-msg muted">{deleteMsg}</p>}
            </form>
          )}
        </BottomSheet>
      )}

      {activeSheet === 'ios-install' && (
        <BottomSheet
          icon={<FiDownload size={20} />}
          iconColor="yellow"
          title={t('installAppTitle')}
          subtitle={t('installAppDesc')}
          onClose={() => setActiveSheet(null)}
        >
          <ol style={{ margin: 0, paddingLeft: 20, color: '#C4CDEB', fontSize: 13, lineHeight: 1.8 }}>
            <li>{t('installStep1')} <FiShare size={13} style={{ verticalAlign: 'middle' }} /></li>
            <li>{t('installStep2')} <FiChevronDown size={13} style={{ verticalAlign: 'middle' }} /></li>
            <li>{t('installStep3')}</li>
            <li>{t('installStep4')}</li>
          </ol>
        </BottomSheet>
      )}
    </div>
  );
}